import React, { useEffect, useState, useRef } from "react";
import { Box, Card, Typography, Skeleton, Button, CardMedia, IconButton, Avatar, Chip } from "@mui/material";
import { GraduationCap, ArrowRight, DollarSign, Star, ChevronLeft, ChevronRight, Users, Clock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

const formatPrice = (price) => {
  if (price === undefined || price === null) return null;
  return `$${parseFloat(price).toFixed(2)}`;
};

const getExpiryText = (expirationDate) => {
  if (!expirationDate) return null;
  const now = new Date();
  const exp = new Date(expirationDate);
  const diff = exp - now;
  if (diff <= 0) return 'Expirado';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  if (hours > 0) return `${hours}h`;
  return 'Pocos minutos';
};

const RecommendedCourses = ({ proActivo = false }) => {
  const [courses, setCourses] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -280, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 280, behavior: 'smooth' });
  };

  useEffect(() => {
    fetch("https://apiweb.hitpoly.com/ajax/communityWidgetsController.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getRecommendedCourses" })
    })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data && d.data.length > 0) {
          const sorted = [...d.data].sort((a, b) => (b.personas_inscritas || 0) - (a.personas_inscritas || 0));
          setCourses(sorted);
        } else {
          setCourses([]);
        }
      })
      .catch(() => {
        setCourses([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton variant="rectangular" height={430} sx={{ mb: 2, borderRadius: 4 }} />;
  if (!courses || courses.length === 0) return null;

  return (
    <Card sx={{
      mb: 3,
      bgcolor: "rgba(255,255,255,0.9)",
      backdropFilter: "blur(8px)",
      borderRadius: 4,
      border: '1px solid #f0f0f0',
      boxShadow: 'none',
      p: 2.5,
      minHeight: 430
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1a1a1a' }}>
          <GraduationCap size={18} color="#0a66c2" /> Cursos
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={scrollLeft} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronLeft size={16} /></IconButton>
          <IconButton size="small" onClick={scrollRight} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronRight size={16} /></IconButton>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto', className: 'community-wall-scroll', scrollbarWidth: 'thin', scrollbarColor: '#0B8DB5 transparent',
        pb: 1.5,
        mx: -0.5,
        px: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#0B8DB5', borderRadius: 4, border: 0 }
      }}>
        {courses.map(course => (
          <Card key={course.id} sx={{
            minWidth: 260,
            maxWidth: 260,
            borderRadius: 3,
            border: '1px solid #eef3f8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            bgcolor: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              borderColor: '#0a66c2'
            }
          }} onClick={() => navigate(`/systems/academia/curso/${course.id}?cursoId=${course.id}`)}>

            <Box sx={{ height: 130, bgcolor: '#f5f5f5', position: 'relative' }}>
              {course.image ? (
                <CardMedia component="img" height="130" image={course.image} alt={course.titulo} />
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                  <GraduationCap size={40} />
                </Box>
              )}
            </Box>

            <Box sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.2 }}>

              <Typography variant="body2" fontWeight="800" color="#1a1a1a" sx={{
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: 36
              }}>
                {course.titulo}
              </Typography>

              {course.descripcion && (
                <Typography variant="caption" color="text.secondary" sx={{
                  fontWeight: 400, fontSize: '0.7rem', lineHeight: 1.3,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                }}>
                  {course.descripcion}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {course.profesor_nombre && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Avatar src={course.profesor_avatar} sx={{ width: 18, height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#6C4DE2' }}>
                      {course.profesor_nombre?.charAt(0)}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                      {course.profesor_nombre}
                    </Typography>
                  </Box>
                )}
                {course.personas_inscritas > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontWeight: 500, fontSize: '0.65rem' }}>
                    <Users size={11} /> {course.personas_inscritas}
                  </Typography>
                )}
                {course.fecha_limite_inscripcion && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: '#FFF3E0', color: '#E65100', px: 0.6, py: 0.15, borderRadius: '5px' }}>
                    <Clock size={10} />
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.55rem', whiteSpace: 'nowrap' }}>
                      {getExpiryText(course.fecha_limite_inscripcion)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                {course.nivel && (
                  <Chip label={course.nivel} size="small" sx={{
                    fontWeight: 700, fontSize: '0.6rem', height: 20,
                    bgcolor: '#3B207F15', color: '#3B207F'
                  }} />
                )}
                {course.tipo_clase && (
                  <Chip label={course.tipo_clase} size="small" sx={{
                    fontWeight: 700, fontSize: '0.6rem', height: 20,
                    bgcolor: '#3B207F15', color: '#3B207F'
                  }} />
                )}
                {course.precioreal !== undefined && course.precioreal !== null && (
                  <Typography variant="body2" fontWeight="800" sx={{ color: '#2E7D32', fontSize: '0.85rem' }}>
                    {course.precioreal == 0 ? 'Gratis' : formatPrice(course.precioreal)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Card>
        ))}
      </Box>

      <Button
        onClick={() => navigate('/systems/academia/cursos/Todas?categoria=Todas')}
        sx={{
          width: '100%',
          mt: 1,
          textTransform: 'none',
          fontWeight: '700',
          color: '#0a66c2',
          '&:hover': { bgcolor: '#eef3f8' }
        }}
        endIcon={<ArrowRight size={16} />}
      >
        Ver todos los cursos
      </Button>
    </Card>
  );
};

export default RecommendedCourses;


